import { Avatar } from '@/components/ui/avatar';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import type { Conversation } from '@fluenceur/types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { NAV_DARK_FOCUS_RING } from '@/components/scaffold/layout/nav-dark';
import NavTag from '@/components/scaffold/nav-bar/components/NavTag';
import LatestMessage from './LatestMessage';
import { getConversationPreview, isNativeConversation } from '@/lib/conversation-selectors';

interface ConversationItemProps {
  conversation: Conversation;
  isSelected: boolean;
  onClick: () => void;
}

const ConversationItem = ({ conversation, isSelected, onClick }: ConversationItemProps) => {
  const { i18n, t } = useTranslation();

  // The server names every thread, from the social account or from the creator
  // identity. The row shows that name verbatim so it matches the open thread's
  // header; a nameless row is a defect to fix upstream, not a label to invent.
  const participant = conversation.participant;
  const displayName = participant?.name ?? '';

  // The row is dated on whichever tip is more recent: a thread of pure watching
  // has no message, and dating it on the thread creation reads as stale.
  const timeAgo = formatRelativeTime(getConversationPreview(conversation).at, i18n.language);

  return (
    <chakra.button
      type="button"
      aria-label={t('messages.thread.open', { name: displayName })}
      aria-current={isSelected ? 'page' : 'false'}
      cursor="pointer"
      // A thread row is a menu row: the selected one takes the active wash, the
      // others light up on hover, and no divider cuts the list into a table.
      bg={isSelected ? 'navDark.activeBg' : 'transparent'}
      border="1px solid"
      borderColor={isSelected ? 'navDark.activeBorder' : 'transparent'}
      rounded="10px"
      onClick={onClick}
      position="relative"
      transition="background-color 0.15s"
      _hover={{ bg: isSelected ? 'navDark.activeBg' : 'navDark.hoverBg' }}
      _focusVisible={NAV_DARK_FOCUS_RING}
      py={2.5}
      px={2.5}
      textAlign="left"
      w="full"
    >
      <Flex gap={2} align="start">
        <Avatar
          size="2xs"
          name={displayName}
          src={participant?.avatarUrl || undefined}
          fallback={displayName.charAt(0)}
          colorPalette="primary"
          fontWeight="semibold"
          css={{
            width: '24px',
            height: '24px',
            fontSize: '11px',
            marginTop: '2px',
            '& span': { display: 'flex', alignItems: 'center', justifyContent: 'center' },
          }}
        />
        <Box flex="1" minW={0}>
          <Flex justify="space-between" align="baseline" gapX={2}>
            <Text
              fontFamily="heading"
              fontWeight={600}
              color="navDark.textStrong"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              fontSize="sm"
              minW={0}
              flex="1"
            >
              {displayName}
            </Text>
            <Text fontSize="xs" color="navDark.textSubtle" flexShrink={0}>
              {timeAgo}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center" gapX={2}>
            <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" flex="1" minW={0}>
              <LatestMessage conversation={conversation} isNew={conversation.isNew || false} />
            </Box>
            {conversation.unreadCount > 0 && <NavTag tone="live">New</NavTag>}
          </Flex>
          {isNativeConversation(conversation) && (
            <Box mt={1}>
              <NavTag tone="ai">{t('messages.channel.creatorApp')}</NavTag>
            </Box>
          )}
        </Box>
      </Flex>
    </chakra.button>
  );
};

export default React.memo(ConversationItem);
